import { Tube, Tubes } from "@contexts/MixerContext";

export const DEPARTMENTS: { label: string; value: string }[] = [
  { label: "Product Use Questions", value: "techsupport" },
  { label: "Customer Services - Orders", value: "custservmgr" },
  { label: "US Sales", value: "sales" },
  { label: "Environment, Health, and Safety", value: "safety" },
  { label: "Custom Lab", value: "customlab" },
  { label: "Education", value: "education" },
  { label: "Marketing", value: "marketing" },
  { label: "Webmaster", value: "webmaster" },
  { label: "Factory Tours", value: "tourguides" },
];

export type DEPARTMENT =
  | "techsupport"
  | "custservmgr"
  | "sales"
  | "safety"
  | "customlab"
  | "education"
  | "marketing"
  | "webmaster"
  | "tourguides"
  | "pr";

export const isDepartment = (value: string): value is DEPARTMENT =>
  ["techsupport", "custservmgr", "sales", "safety", "customlab", "education", "marketing", "webmaster", "tourguides", "pr"].includes(value);

export const DEFAULT_IMAGE = "https://goldenhub8.goldenpaints.com/goldenhub8/assets/86hjfbxqz3k8o0w0";

export const PRODUCT_GROUP = "Product Group";
export const CURATED_CONTENT = "Curated Content";

export const DEFAULT_COLOR_VALUE: Tube = { r: 255, g: 255, b: 255, name: "", paintId: "", quantityHdths: 0 };
export const DEFAULT_EMPTY_TUBES: Tubes = {
  tube1: { r: 255, g: 255, b: 255, name: "", paintId: "", quantityHdths: 0 },
  tube2: { r: 255, g: 255, b: 255, name: "", paintId: "", quantityHdths: 0 },
  tube3: { r: 255, g: 255, b: 255, name: "", paintId: "", quantityHdths: 0 },
  tube4: { r: 255, g: 255, b: 255, name: "", paintId: "", quantityHdths: 0 },
};
export const DEFAULT_MIXED_COLORS = {
  rgb: { r: 255, g: 255, b: 255 },
  lab: { l: 100, a: 0, b: 0 },
  tint1_1: { r: 255, g: 255, b: 255 },
  tint1_3: { r: 255, g: 255, b: 255 },
  tint1_10: { r: 255, g: 255, b: 255 },
  tint3_1: { r: 255, g: 255, b: 255 },
  tint10_1: { r: 255, g: 255, b: 255 },
};

export const DEFAULT_NUMBERIC_ERROR = {
  RGB: "",
  CMYK: "",
};

type BrandConfigType = {
  brandLogo: string;
  brandImagePath?: string;
  url: string;
  slug: string;
  logoAlt: string;
  bgColor: string;
  fullName: string;
  storeLocatorLogo?: string;
  quickMediumPrefix: string; // used to help display product groups in search
  code: string;
  storeLocatorFilterName: string | null; // if null, brand will not show in store locator
};

export const BrandConfig: { [key: string]: BrandConfigType } = {
  "brands-1": {
    brandLogo: "/images/logos/GAC_2021_Lockup_W.svg",
    url: "/",
    slug: "golden-artist-colors",
    logoAlt: "Golden Artist Colors",
    bgColor: "#000000",
    fullName: "Golden Artist Colors",
    quickMediumPrefix: "GAC",
    code: "gac",
    storeLocatorFilterName: null,
  },
  "brands-2": {
    brandLogo: "/images/logos/WB_2021_Lockup_W.svg",
    brandImagePath: "brands/williamsburg.jpg",
    url: "/products/williamsburg-artist-oil-colors",
    slug: "williamsburg-artist-oil-colors",
    logoAlt: "Williamsburg Artist Oils",
    bgColor: "#6E182E",
    fullName: "Williamsburg Artist Oils",
    quickMediumPrefix: "Oil",
    code: "wb",
    storeLocatorLogo: "/images/store-locator/W_icon18x18.gif",
    storeLocatorFilterName: "oils",
  },
  "brands-3": {
    brandLogo: "/images/logos/QoR_2021_Lockup_W.svg",
    brandImagePath: "brands/qor.jpg",
    url: "/products/qor-artist-watercolors",
    slug: "qor-artist-watercolors",
    logoAlt: "QoR Artist Watercolors",
    bgColor: "#182632",
    fullName: "QoR Artist Watercolors",
    quickMediumPrefix: "Watercolor",
    code: "qor",
    storeLocatorLogo: "/images/store-locator/Q_icon18x18.gif",
    storeLocatorFilterName: "watercolors",
  },
  "brands-4": {
    brandLogo: "/images/logos/GACAcrylics_2021_Lockup_W.svg",
    brandImagePath: "brands/acrylic.jpg",
    url: "/products/golden-artist-acrylics",
    slug: "golden-artist-acrylics",
    logoAlt: "Golden Artist Acrylics",
    bgColor: "#312F2E",
    fullName: "Golden Artist Acrylics",
    quickMediumPrefix: "Acrylic",
    code: "gaa",
    storeLocatorLogo: "/images/store-locator/G_icon18x18.gif",
    storeLocatorFilterName: "golden-acrylics",
  },
  "brands-5": {
    brandLogo: "/images/logos/PPAP_w_lockup.svg",
    brandImagePath: "brands/panpastel.jpg",
    url: "/products/panpastel-artist-pastels",
    slug: "panpastel-artist-pastels",
    logoAlt: "PanPastel Artist Pastels",
    bgColor: "#163734",
    fullName: "PanPastel Artist Pastels",
    quickMediumPrefix: "PanPastel",
    code: "pp",
    storeLocatorLogo: "/images/store-locator/P_icon18x18.gif",
    storeLocatorFilterName: "panpastel",
  },
  "brands-6": {
    brandLogo: "/images/brands/sagg/logo.svg",
    url: "/sam-adele-golden-gallery",
    slug: "sam-adele-golden-gallery",
    logoAlt: "Sam Adele Golden Gallery",
    bgColor: "#225522",
    fullName: "Sam Adele Golden Gallery",
    quickMediumPrefix: "SAGG",
    code: "sagg",
    storeLocatorFilterName: null, // No store locator for SAGG
  },
};

export const BrandConfigByCode: { [key: string]: BrandConfigType } = {
  gac: BrandConfig["brands-1"],
  wb: BrandConfig["brands-2"],
  qor: BrandConfig["brands-3"],
  gaa: BrandConfig["brands-4"],
  pp: BrandConfig["brands-5"],
  sagg: BrandConfig["brands-6"],
};

export const BrandMapByName: { [key: string]: string } = {
  "Williamsburg Handmade Oil Colors": "wb",
  "QoR Watercolors": "qor",
  Golden: "gaa",
  PanPastel: "pp",
};
